/**
 * @file cursor-manager.ts
 * @description Handles canvas cursor state transitions based on active tools and selection handle hovers.
 */

import { g, Tool } from '@hcie/core';
import { ShapeSelectionHandler } from './handlers/shape-selection-handler';
import { PolySelectionHandler } from './handlers/poly-selection-handler';
import { getActiveLayer } from './layer-service';

/**
 * @function updateCanvasCursor
 * @description Updates the CSS cursor property of the drawing canvas based on the current tool and hover state.
 * @returns {void}
 */
export function updateCanvasCursor(): void {
    // [CRITICAL_LOGIC_START]
    const drawingCanvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
    if (!drawingCanvas) {
        return;
    }

    const currentToolId: string = g.current_tool?.id || '';
    
    // STEP 1: Drawing Tool Cursor Management
    // For drawing tools, we hide the browser cursor as the render engine draws a preview circle.
    const drawingToolIds: readonly string[] = [
        Tool.Pen.id, 
        Tool.Brush.id, 
        Tool.Eraser.id, 
        Tool.Spray.id, 
        'btn-red-eye'
    ];
    
    if (drawingToolIds.includes(currentToolId)) {
        const activeLayer = getActiveLayer();
        if (activeLayer?.type === 'vector') {
            // "X" cursor via CSS not-allowed or custom SVG
            drawingCanvas.style.cursor = 'not-allowed';
            return;
        }
        
        drawingCanvas.style.cursor = 'none';
        return;
    }

    // STEP 2: Selection Handle Hover Cursors
    // Check if the mouse is hovering over a transformation handle in selection handlers.
    const shapeHoverCursor: string | null = ShapeSelectionHandler.getHoverCursor();
    if (shapeHoverCursor) {
        drawingCanvas.style.cursor = shapeHoverCursor;
        return;
    }

    const polyHoverCursor: string | null = PolySelectionHandler.getHoverCursor();
    if (polyHoverCursor) {
        drawingCanvas.style.cursor = polyHoverCursor;
        return;
    }

    // STEP 3: Default Tool Cursors
    const selectionToolIds: readonly string[] = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-lasso', 'btn-poly-select', 'btn-move-selection', 'btn-move-content'
    ];
    
    if (selectionToolIds.includes(currentToolId)) {
        drawingCanvas.style.cursor = 'crosshair';
    } else if (currentToolId === Tool.Eye_Dropper.id) {
        drawingCanvas.style.cursor = 'cell';
    } else {
        drawingCanvas.style.cursor = 'default';
    }
    // [CRITICAL_LOGIC_END]
}
